import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { forLifeOf, ignoreNullish, LifecycleAware } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import type { ChartOptions } from 'chart.js';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentsSummaryDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/payments-summary-dto';

@LifecycleAware()
@Component({
    selector: 'account-payments-summary-tile',
    styleUrls: ['./payment-summary-tile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <card class="payment-summary-card">
            <div card-header>
                <h3
                    id="payment-summary-card-heading"
                    i18n="Header for the Open Balance tile in the Account Experience"
                >
                    Open Balance
                </h3>
            </div>
            <div class="payment-summary-card-body" card-body>
                <lsq-circle-loader *ngIf="dataSource.loading$ | async"></lsq-circle-loader>
                <div class="payment-summary-card-body-inner" *ngIf="!(dataSource.loading$ | async)">
                    <ng-container *ngIf="paymentDataIsZero$ | async">
                        <div class="payment-summary-chart payment-summary-chart-no-data">
                            <div class="chart-center">
                                {{ noPaymentDataPlaceholderI18n }}
                            </div>
                            <lsq-chart
                                class="doughnut-chart"
                                [options]="chartOptions"
                                [colors]="[blankChartColor]"
                                [labels]="['No Data']"
                                [data]="[100]"
                            >
                            </lsq-chart>
                        </div>
                    </ng-container>

                    <ng-container *ngIf="!(paymentDataIsZero$ | async)">
                        <div class="payment-summary-chart">
                            <lsq-chart
                                class="doughnut-chart"
                                [options]="chartOptions"
                                [colors]="chartColors"
                                [labels]="chartLabels"
                                [data]="chartData$ | async | notNull"
                            >
                            </lsq-chart>
                        </div>
                    </ng-container>

                    <div class="chart-legend">
                        <ng-container
                            *ngFor="let moneyAmount of chartLegendData$ | async; let i = index"
                        >
                            <div class="chart-legend-section">
                                <div class="chart-legend-label">
                                    <div
                                        *ngIf="!!chartColors[i]"
                                        class="chart-legend-dot"
                                        [style.background-color]="chartColors[i]"
                                    ></div>
                                    {{ chartLabels[i] }}
                                </div>
                                <div class="chart-legend-value">
                                    {{ moneyAmount | lsqCurrency }}
                                </div>
                            </div>
                        </ng-container>
                    </div>
                </div>
            </div>
        </card>
    `
})
export class AccountPaymentsSummaryTileComponent implements OnInit, OnDestroy {
    @Input() public dataSource!: LsqDataSource<PaymentsSummaryDto>;
    public paymentDataIsZero$!: Observable<boolean>;
    public chartData$!: Observable<number[]>;
    public chartLegendData$!: Observable<number[]>;

    public readonly chartOptions: ChartOptions = {
        cutoutPercentage: 60,
        aspectRatio: 1,
        responsive: true,
        maintainAspectRatio: true,
        tooltips: {
            enabled: false
        }
    };
    public readonly chartColors: string[] = ['#008661', '#F65841', '#3875BE', ''];
    public readonly chartLabels: string[] = [
        this._i18n({
            value: 'Current Balance Due',
            meaning: 'Total amount due as of now'
        }),
        this._i18n({
            value: 'Late Fees',
            meaning: 'Total amount late fees charged on past due invoices'
        }),
        this._i18n({
            value: 'Remaining Balance',
            meaning: 'Total amount due later'
        }),
        this._i18n({
            value: 'Your Total Balance',
            meaning: 'Total amount due'
        })
    ];

    public readonly noPaymentDataPlaceholderI18n = this._i18n({
        value: 'There is no payment data to display.',
        meaning:
            'Placeholder text for the Payment Summary tile that appears when no payment data is found or received',
        description: 'Placeholder text'
    });

    public readonly blankChartColor = '#eef0f7';

    constructor(private _i18n: I18n) {}

    public ngOnInit(): void {
        this.paymentDataIsZero$ = this.dataSource.data$.pipe(
            map(
                (paymentsSummaryDto) =>
                    paymentsSummaryDto.currentBalance === 0 &&
                    paymentsSummaryDto.lateFees === 0 &&
                    paymentsSummaryDto.remainingBalance === 0 &&
                    paymentsSummaryDto.totalBalance === 0
            )
        );
        this.chartData$ = this.dataSource.data$.pipe(
            forLifeOf(this),
            ignoreNullish(),
            map((data) => [data.currentBalance!!, data.lateFees!!, data.remainingBalance!!])
        );
        this.chartLegendData$ = this.dataSource.data$.pipe(
            forLifeOf(this),
            ignoreNullish(),
            map((data) => [
                data.currentBalance!!,
                data.lateFees!!,
                data.remainingBalance!!,
                data.totalBalance!!
            ])
        );
    }

    public ngOnDestroy(): void {}
}
